/**
 * Displays a non-binding local-currency reference price while preserving EUR
 * as the binding Shopify and checkout currency.
 *
 * Source: European Commission InforEuro, July 2026 monthly accounting rates.
 * 1 EUR equals the stored rate in the target currency.
 */

const RATE_VALID_UNTIL = new Date('2026-08-30T23:59:59Z');

const EURO_REFERENCE_RATES = Object.freeze({
  AED: 4.1812,
  AFN: 73.61,
  ALL: 94.215,
  AMD: 421,
  AOA: 1045.9,
  ARS: 1685.2365,
  AUD: 1.6531,
  AWG: 2.06449,
  AZN: 1.93902,
  BAM: 1.95583,
  BBD: 2.29728,
  BDT: 140.2938,
  BHD: 0.43001,
  BIF: 3396.053,
  BMD: 1.1406,
  BND: 1.47315,
  BOB: 7.89866,
  BRL: 5.8899,
  BSD: 1.1406,
  BWP: 15.43579,
  BYN: 3.30935,
  BZD: 2.29397,
  CAD: 1.6201,
  CDF: 2634.265,
  CHF: 0.9222,
  CLP: 1052.60271,
  CNY: 7.7492,
  COP: 3923.0937,
  CRC: 517.4503,
  CVE: 110.265,
  CZK: 24.262,
  DJF: 203.11235,
  DKK: 7.4745,
  DOP: 67.70065,
  DZD: 151.8125,
  EGP: 56.36219,
  ERN: 17.19455,
  ETB: 180.645,
  EUR: 1,
  FJD: 2.57963,
  FKP: 0.86215,
  GBP: 0.86215,
  GEL: 3.01205,
  GHS: 12.8355,
  GIP: 0.86215,
  GMD: 84.525,
  GNF: 9979.785,
  GTQ: 8.70164,
  GYD: 238.13,
  HKD: 8.9449,
  HNL: 30.52474,
  HUF: 354.33,
  IDR: 20362.79,
  ILS: 3.3942,
  INR: 107.838,
  IQD: 1494.186,
  ISK: 144,
  JMD: 179.25255,
  JOD: 0.80869,
  JPY: 184.62,
  KES: 147.365,
  KGS: 99.77644,
  KHR: 4599,
  KMF: 492.615,
  KRW: 1760.8,
  KWD: 0.35119,
  KYD: 0.9467,
  KZT: 553.415,
  LAK: 25532.615,
  LBP: 102140.73,
  LKR: 382.787,
  LRD: 207.5892,
  LSL: 18.7233,
  LYD: 7.3081,
  MAD: 10.6916,
  MDL: 19.822,
  MGA: 4853,
  MKD: 61.66,
  MMK: 4522.479,
  MNT: 4084.4886,
  MOP: 9.21833,
  MRU: 45.4333,
  MUR: 53.785,
  MVR: 17.63368,
  MWK: 1973.6116,
  MXN: 19.9324,
  MYR: 4.6434,
  MZN: 72.395,
  NGN: 1562.4,
  NIO: 41.97408,
  NOK: 11.3295,
  NPR: 171.9439,
  NZD: 2.0175,
  OMR: 0.43911,
  PEN: 3.90279,
  PGK: 5.08402,
  PHP: 69.786,
  PKR: 316.72,
  PLN: 4.2873,
  PYG: 6946.19127,
  QAR: 4.15178,
  RON: 5.2418,
  RSD: 117.38,
  RUB: 89.7913,
  RWF: 1668.37,
  SAR: 4.2833,
  SBD: 9.14662,
  SCR: 16.95965,
  SDG: 3849.582,
  SEK: 11.0865,
  SGD: 1.4754,
  SHP: 0.86215,
  SLE: 28.05876,
  SOS: 650.595,
  SRD: 42.79303,
  SSP: 5343.85665,
  STN: 24.5,
  SZL: 18.7233,
  THB: 37.919,
  TJS: 10.55055,
  TMT: 3.9921,
  TND: 3.3605,
  TOP: 2.73,
  TRY: 53.2001,
  TTD: 7.73915,
  TWD: 36.26945,
  TZS: 2993.99,
  UAH: 51.12283,
  UGX: 4189,
  USD: 1.1406,
  UYU: 45.89204,
  UZS: 13697.79617,
  VES: 709.73096,
  VND: 29997.78,
  VUV: 136.405,
  WST: 3.1275,
  XAF: 655.957,
  XCD: 3.09103,
  XCG: 2.04167,
  XOF: 655.957,
  XPF: 119.355,
  YER: 608.5101,
  ZAR: 18.7233,
  ZMW: 20.51875,
});

const readEuroAmount = (minorUnits) => {
  const value = Number(minorUnits);
  return Number.isFinite(value) && value >= 0 ? value / 100 : null;
};

const formatCurrency = (amount, currencyCode) => {
  const locale = document.documentElement.lang || 'de-DE';
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'narrowSymbol',
  }).format(amount);

  return formatted.includes(currencyCode) ? formatted : `${formatted} ${currencyCode}`;
};

class LocalReferencePrice extends HTMLElement {
  connectedCallback() {
    this.updateReferencePrice();
  }

  updateReferencePrice() {
    const priceContainer = this.closest('[ref="priceContainer"]');
    const baseCurrency = this.dataset.baseCurrency;
    const referenceCurrency = this.dataset.referenceCurrency;
    const rate = EURO_REFERENCE_RATES[referenceCurrency];
    const price = readEuroAmount(this.dataset.priceMinor);

    if (
      !priceContainer ||
      baseCurrency !== 'EUR' ||
      referenceCurrency === 'EUR' ||
      !Number.isFinite(rate) ||
      rate <= 0 ||
      price === null ||
      new Date() > RATE_VALID_UNTIL
    ) {
      priceContainer?.classList.remove('local-reference-price-active');
      this.hidden = true;
      return;
    }

    const referencePrice = this.querySelector('[data-reference-price]');
    const referenceCompareAtPrice = this.querySelector('[data-reference-compare-at-price]');
    const hasVolumePricing = this.dataset.hasVolumePricing === 'true';
    const priceMin = readEuroAmount(this.dataset.priceMinMinor);
    const priceMax = readEuroAmount(this.dataset.priceMaxMinor);
    const compareAtPrice = readEuroAmount(this.dataset.compareAtPriceMinor);

    if (!referencePrice || !referenceCompareAtPrice) return;

    if (hasVolumePricing && priceMin !== null && priceMax !== null && priceMin > 0 && priceMax > 0) {
      referencePrice.textContent = `ca. ${formatCurrency(priceMin * rate, referenceCurrency)} – ${formatCurrency(
        priceMax * rate,
        referenceCurrency
      )}`;
    } else {
      referencePrice.textContent = `ca. ${formatCurrency(price * rate, referenceCurrency)}`;
    }

    if (compareAtPrice !== null && compareAtPrice > price) {
      referenceCompareAtPrice.textContent = `ca. ${formatCurrency(compareAtPrice * rate, referenceCurrency)}`;
      referenceCompareAtPrice.hidden = false;
    } else {
      referenceCompareAtPrice.hidden = true;
      referenceCompareAtPrice.textContent = '';
    }

    priceContainer.classList.add('local-reference-price-active');
    this.hidden = false;
  }
}

if (!customElements.get('local-reference-price')) {
  customElements.define('local-reference-price', LocalReferencePrice);
}
